import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FaPlus } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { closeChangePasswordModal } from '@/store/slices/modalSlice';
import { useChangePasswordMutation } from '@/store/api/authApi';
import CustomInput from '../common/CustomInput';
import useOutsideClick from '@/hooks/useOutsideClick';
import useModalAnimation from '@/hooks/useModalAnimation';
import { ApiError } from '@/types/error';
import { Icons } from '@/utils/icons';
import Loader from '@/components/common/Loader';

const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Old password required'),
    newPassword: z
      .string()
      .min(1, 'New password required')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).*$/,
        'Password must contain at least 1 uppercase, 1 lowercase and 1 number'
      ),
    confirmPassword: z.string().min(1, 'Confirm password required'),
  })
  .refine((data) => data.newPassword !== data.oldPassword, {
    message: 'New password must be different from old password',
    path: ['newPassword'],
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'The Confirm Password field confirmation does not match',
    path: ['confirmPassword'],
  });

type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

const ChangePasswordModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((state) => state.modal.isChangePasswordModalOpen);
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const modalRef = useRef<HTMLDivElement>(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const { animate, handleClose } = useModalAnimation({
    isOpen,
    onClose: () => {
      reset();
      dispatch(closeChangePasswordModal());
    },
  });
  useOutsideClick(modalRef, handleClose);

  if (!isOpen) return null;

  const onSubmit = async (data: ChangePasswordForm) => {
    try {
      const response = await changePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      }).unwrap();
      const successMessage =
        (response as { message?: string })?.message || 'Password changed successfully';
      toast.success(successMessage);
      handleClose();
    } catch (error) {
      const message = (error as ApiError)?.data?.message || 'Failed to change password';
      toast.error(message);
    }
  };

  return createPortal(
    <div className="fixed font-roboto inset-0 z-[999] flex items-start justify-center bg-black/50 pt-10">
      {isLoading && <Loader />}
      <div
        ref={modalRef}
        className={`w-full sm:max-w-[500px] bg-white shadow-md shadow-black/50 transform transition-all duration-300 ease-out ${animate ? 'translate-y-0 opacity-100' : '-translate-y-6 opacity-0'}`}
      >
        {/* Header - bg-primary (Yellow) */}
        <div className="bg-primary px-2 py-2 flex justify-between items-center border-b border-black/10">
          <h2 className="text-base xl:text-xl xl:font-medium text-gray-900">Change Password</h2>
          <button onClick={handleClose} className="hover:opacity-70 transition-opacity">
            <Icons.plusBold size={17} className="text-black rotate-45" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-2 pb-5">
          <CustomInput
            name="oldPassword"
            control={control}
            label="Old Password"
            type="password"
            className="mb-1"
            labelClassName="!mb-1"
            inputClassName="bg-[#ebf0f5] border h-10 2xl:h-[46px] px-3 placeholder:text-base"
            passwordToggleClassName="w-12 h-10 2xl:h-[46px] text-white text-lg inline-flex justify-center items-center bg-primary-dark-grey right-0"
          />

          <CustomInput
            name="newPassword"
            control={control}
            label="New Password"
            type="password"
            className="mb-1"
            labelClassName="!mb-1"
            inputClassName="bg-[#ebf0f5] border h-10 2xl:h-[46px] px-3"
            passwordToggleClassName="w-12 h-10 2xl:h-[46px] text-white text-lg inline-flex justify-center items-center bg-primary-dark-grey right-0"
          />

          <CustomInput
            name="confirmPassword"
            control={control}
            label="Confirm Password"
            type="password"
            labelClassName="!mb-1"
            inputClassName="bg-[#ebf0f5] border h-10 2xl:h-[46px] px-3"
            className="mb-2"
            passwordToggleClassName="w-12 h-10 2xl:h-[46px] text-white text-lg inline-flex justify-center items-center bg-primary-dark-grey right-0"
          />

          {/* Action Button - Dark Green/Teal */}
          <button
            type="submit"
            disabled={!isDirty || !isValid || isLoading}
            className="w-full disabled:bg-green-800/50 disabled:text-white disabled:hover:text-black/50 disabled:hover:bg-primary/50 
            disabled:cursor-not-allowed bg-primary text-black py-1.5 text-base font-arial transition-all shadow-sm"
          >
            <span className="inline-flex items-center justify-center gap-2">
              {isLoading && <Icons.loading className="animate-spin" />}
              Change Password
            </span>
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
